/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    parseAbsolute,
    parseDateTime,
    parseTime,
    parseZonedDateTime,
} from '@internationalized/date';

export type Gnoment = object;
// export interface Gnoment extends Object {}  // use this later if you need other fields

export function parseDateTimeWithAbsoluteAndZoned(input) {
    input = stripSpaceIfAbsoluteOrZoned(input);
    input = addBracketsIfZoned(input);
    if (needsZoned(input)) {
        return parseZonedDateTime(input);
    }
    if (needsAbsolute(input)) {
        return parseAbsolute(input);
    }
    return parseDateTime(input);
}

export function parseJustTimeStr(str) {
    const isPm = hasPmSuffix(str);
    str = stripAmPmSuffix(str);
    str = putBackLeadingZeroIfNeeded(str);
    let ret = parseTime(str);
    if (isPm) {
        ret = ret.add({ hours: 12 });
    }
    return ret;
}

export function isJustTimeStr(str) {
    // examples:
    //  10:00 AM
    //  09:45:01 PM  <-- len 11
    //  9:45:01 PM
    //  14:45:02
    //  08:30
    //  8:30

    // have to strip the AM/PM:
    //  Invalid ISO 8601 time string: 10:00 AM
    str = stripAmPmSuffix(str);

    // so now it's really just matching these:
    //  10:00
    //  09:45:01
    //  9:45:01
    //  14:45:02
    //  08:30
    //  8:30
    if (str.length !== 4 && str.length !== 5 && str.length !== 7 && str.length !== 8) {
        return false;
    }

    const frontMatches = str.match(/^[0-2]?\d:\d\d/);
    if (!frontMatches || frontMatches.length === 0) {
        return false;
    }
    if (str.length === 4 || str.length === 5) {
        // ex: 08:30 or 8:30
        return true;
    }

    // ex: 14:45:02
    const backMatches = str.substring(frontMatches[0].length).match(/:\d\d/);
    if (backMatches && backMatches.length === 1) {
        return true;
    }
    return false;
}

function hasPmSuffix(str) {
    return str.toUpperCase().endsWith(' PM');
}

function stripAmPmSuffix(str) {
    str = str.toUpperCase();
    if (str.endsWith(' AM') || str.endsWith(' PM')) {
        return str.substring(0, str.length - 3);
    }
    return str;
}

function putBackLeadingZeroIfNeeded(str) {
    // Error: Invalid ISO 8601 time string: 9:45:01
    if (str[1] === ':') {
        return `0${str}`;
    }
    return str;
}

function getPlusOrMinusIndexAfterT(str) {
    // examples:
    //      2024-01-26T10:12:59.210241-05:00
    //      2025-03-06T21:25:26 +0000
    //      2025-03-06T21:25:26+0000[UTC]
    if (str.length < 11) {
        return -1;
    }
    const plusIndex = str.indexOf('+', 11);
    if (plusIndex !== -1) {
        return plusIndex;
    }
    return str.indexOf('-', 11);
}

function needsZoned(str) {
    // example: 2025-03-06T21:25:26+0000[UTC]
    const plusOrMinusIndex = getPlusOrMinusIndexAfterT(str);
    if (plusOrMinusIndex === -1) {
        return false;
    }
    const bracketStartIndex = str.indexOf('[', plusOrMinusIndex + 1);
    if (bracketStartIndex === -1) {
        return false;
    }
    return str.endsWith(']');
}

function needsAbsolute(str) {
    // examples:
    //      2024-01-26T10:12:59.210241-05:00
    //      2025-03-06T21:25:26 +0000
    if (str.length < 11) {
        return false;
    }
    const plusOrMinusIndex = getPlusOrMinusIndexAfterT(str);
    return plusOrMinusIndex !== -1;
}

export function stripDayOfWeekPrefix(str) {
    // example: Tue Apr 01, 2025
    let strUpper = str.toUpperCase();
    const stripAnyPrefix = [
        'MONDAY',
        'MON',
        'TUESDAY',
        'TUE',
        'TUES',
        'WEDNESDAY',
        'WED',
        'WEDS',
        'THURSDAY',
        'THU',
        'THUR',
        'THURS',
        'FRIDAY',
        'FRI',
        'SATURDAY',
        'SAT',
        'SUNDAY',
        'SUN',
    ];

    stripAnyPrefix.forEach((prefix) => {
        if (strUpper.startsWith(prefix)) {
            str = str.substring(prefix.length);
            strUpper = str.toUpperCase();
        }
    });

    return str.trim();
}

function stripSpaceIfAbsoluteOrZoned(str) {
    // example: 2025-03-06T21:25:26 +0000 UTC
    const plusOrMinusIndex = getPlusOrMinusIndexAfterT(str);
    if (plusOrMinusIndex === -1) {
        return str;
    }
    if (str[plusOrMinusIndex - 1] !== ' ') {
        return str;
    }
    // strip the space
    return str.substring(0, plusOrMinusIndex - 1) + str.substring(plusOrMinusIndex);
}

function addBracketsIfZoned(str) {
    const plusOrMinusIndex = getPlusOrMinusIndexAfterT(str);
    if (plusOrMinusIndex === -1) {
        return str;
    }
    const spaceAfterPlusOrMinusIndex = str.indexOf(' ', plusOrMinusIndex);
    if (spaceAfterPlusOrMinusIndex === -1) {
        return str;
    }
    // then remove the space and put brackets around the timezone
    return `${str.substring(0, spaceAfterPlusOrMinusIndex)}[${str.substring(
        spaceAfterPlusOrMinusIndex + 1
    )}]`;
}

export function getDaysSinceBeginningOfYear(gn) {
    const firstDayOfYear = gn.startOf('year');
    return gn.diff(firstDayOfYear, 'days') + 1;
}

export function getDateOnYearWithDayOffset(gn, dayOffset) {
    const firstDayOfYear = gn.startOf('year');
    return firstDayOfYear.add(dayOffset - 1, 'days');
}

export function padLeftIfNeeded(str, delim, minLen) {
    while (str.length < minLen) {
        str = `${delim}${str}`;
    }
    return str;
}
